import * as React from 'react'

import { DirectRoutes, GroupedRoutesAirportsType, GrouppedRoute } from 'utils/types'
import { RoutesAside, RoutesFooter } from './RoutesAside'
import { FlightsAside } from './FlightsAside'
import { Map } from './Map'
import { RouteItem } from './RoutesAside'
import { List } from './RoutesAside/Styles'
import { useMedia } from 'utils/hooks'
import { formBreakpoint } from 'globalStyles'

const SHOW_ITEMS = 6
const SHOW_ITEMS_MOBILE = 3

const getMapShapesStyles = (isMobile: boolean) => ({
  disabledColor: 'var(--text-dark)',
  defaultColor: 'var(--text-disabled)',
  hoveredColor: 'var(--warning-700)',
  selectedColor: 'var(--brand-700)',
  disabledMarkerRadius: isMobile ? 2 : 3,
  defaultMarkerRadius: isMobile ? 3 : 5,
  curveWeight: isMobile ? 6 : 3,
  directMarkerColor: 'var(--text-light)',
})

interface Props {
  routes: GroupedRoutesAirportsType | null
  directRoutes: DirectRoutes[] | null
  handleInputChange: (value: string) => void
}

const RoutesScreen = ({
  routes,
  handleInputChange,
  directRoutes,
}: Props) => {
  const [selectedRouteId, setSelectedRouteId] = React.useState<string | null>(null)
  const [hoveredRouteId, setHoveredRouteId] = React.useState<string | null>(null)
  const [page, setPage] = React.useState(0)
  const isMobile = useMedia(`(max-width:${formBreakpoint})`)
  const listRef = React.useRef<HTMLUListElement>(null)

  const itemsToShow = React.useMemo(() => (isMobile ? SHOW_ITEMS_MOBILE : SHOW_ITEMS), [isMobile])

  React.useLayoutEffect(() => {
    setSelectedRouteId(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page])

  React.useLayoutEffect(() => {
    setPage(0)
    setSelectedRouteId(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routes])

  const routesAsideCallbacks = {
    switchPage: React.useCallback(
      (dir: number) =>
        setPage(
          Math.max(
            0,
            Math.min(
              Math.ceil(routes!.routes.length / (isMobile ? SHOW_ITEMS_MOBILE : SHOW_ITEMS)) - 1,
              page + dir
            )
          )
        ),
      [routes, page, isMobile]
    ),
  }

  const routesAsideProps = React.useMemo(
    () =>
      routes?.routes.length
        ? {
            currentCountEnd: Math.min(itemsToShow * (page + 1), routes.routes.length),
            currentCountStart: itemsToShow * page + 1,
            totalCount: routes.routes.length,
          }
        : undefined,
    [routes, page, itemsToShow]
  )

  const routeItemCallbacks = {
    handleClickItem: React.useCallback((id: string) => setSelectedRouteId(id), []),
    handleEnterItem: !isMobile ? (id: string) => setHoveredRouteId(id) : undefined,
    handleLeaveItem: !isMobile ? () => setHoveredRouteId(null) : undefined,
  }

  const routeItems = React.useMemo(
    () =>
      routes?.routes.length
        ? {
            routes: routes.routes.slice(page * itemsToShow, page * itemsToShow + itemsToShow),
          }
        : undefined,
    [routes, page, itemsToShow]
  )

  const flightAsideCallbacks = {
    handleClose: React.useCallback(() => setSelectedRouteId(null), []),
  }

  const flightAsideProps = React.useMemo(() => {
    if (routes) {
      const selectedRoute = routes.routes.find((r) => r.id === selectedRouteId)
      return selectedRoute
        ? {
            airports: Object.values(routes.airports),
            route: selectedRoute,
          }
        : undefined
    }
    return undefined
  }, [selectedRouteId, routes])

  const mapRoutes = React.useMemo(
    () =>
      routes?.routes.length
        ? routes.routes.map((route, i) => {
            route.isVisible = i >= page * itemsToShow && i < page * itemsToShow + itemsToShow
            return route as GrouppedRoute & { isVisible: boolean }
          })
        : undefined,
    [routes, page, itemsToShow]
  )

  const mapDirectRoutes = React.useMemo(() => (directRoutes?.length ? directRoutes : undefined), [
    directRoutes,
  ])

  const mapCallbacks = {
    handleMouseOverRoute: React.useCallback((id: string) => setHoveredRouteId(id), []),
    handleMouseOutRoute: React.useCallback(() => setHoveredRouteId(null), []),
    handleClickRoute: React.useCallback(
      (id: string | null) => setSelectedRouteId(id),
      []
    ),
    handleClickDirectRouteMarker: React.useCallback(
      (route: DirectRoutes) => {
        handleInputChange(route.iata)
      },
      [handleInputChange]
    ),
  }

  return (
    <React.Fragment>
      {routeItems && routesAsideProps ? (
        <RoutesAside>
          <List ref={listRef}>
            {routeItems.routes.map((routeItem) => {
              const isSelected = selectedRouteId === routeItem.id
              const isHovered = hoveredRouteId === routeItem.id
              return (
                <RouteItem
                  key={routeItem.id}
                  route={routeItem}
                  selected={isSelected}
                  hovered={isHovered}
                  {...routeItemCallbacks}
                />
              )
            })}
          </List>
          <RoutesFooter {...routesAsideProps} {...routesAsideCallbacks} />
        </RoutesAside>
      ) : null}

      {flightAsideProps ? <FlightsAside {...flightAsideProps} {...flightAsideCallbacks} /> : null}

      <Map
        canZoom={true}
        routes={mapRoutes}
        directRoutes={mapDirectRoutes}
        selectedRouteId={selectedRouteId}
        hoveredRouteId={hoveredRouteId}
        shapeStyles={getMapShapesStyles(isMobile)}
        padding={[100, 100]}
        paddingMobile={[0, 100]}
        {...mapCallbacks}
      />
    </React.Fragment>
  )
}

export default React.memo(RoutesScreen)
